/**
=========================================================
* Soft UI Dashboard PRO React - v2.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-material-ui
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// Core
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Components
import Box from "components/Atoms/Box";
import BoxV1 from "components/Atoms/Box/BoxV1";
import ButtonV1 from "components/Atoms/Button/ButtonV1";
import TypographyV1 from "components/Atoms/Typography/TypographyV1";
import CoverLayout from "components/LayoutContainers/CoverLayout";
import Credentials from "pages/Auth/SignUp/Credentials";
import Logo from "components/Logo";
import Icon from "@mui/material/Icon";

// Images
import curved11 from "assets/images/curved-images/auth2.png";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { openNotificationPopup, selectThemeMode } from "state/application/applicationSlice";
import { useSignupMutation } from "state/auth/authApiSlice";

function SignUp() {
  const { t } = useTranslation(["auth"]);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const themeMode = useSelector(selectThemeMode);
  const isDark = themeMode === "dark";

  const [credentials, setCredentials] = useState({
    name: "",
    email: "",
    password: "",
    agreement: false,
  });
  const [buttonText, setButtonText] = useState(t("signup.submit"));

  // CREATE user
  const [signup, { isLoading }] = useSignupMutation();

  const handleSubmit = async () => {
    setButtonText(t("signup.submit_working"));
    try {
      await signup({
        name: credentials.name,
        email: credentials.email,
        password: credentials.password,
        type: "ADMIN",
      }).unwrap();

      dispatch(
        openNotificationPopup({
          notification: t("signup.verification_sent"),
          type: "success",
        })
      );
      navigate("/signin");
    } catch (err) {
      dispatch(openNotificationPopup({ notification: err, type: "error" }));
      setButtonText(t("signup.submit"));
    }
  };

  const canSave =
    [credentials.name, credentials.email, credentials.password, credentials.agreement].every(
      Boolean
    ) && !isLoading;

  return (
    <CoverLayout
      image={curved11}
      header={
        <Box mb={2}>
          <Box mb={2}>
            <Logo size="medium" showSubtitle />
          </Box>
          <TypographyV1
            variant="h3"
            fontWeight="bold"
            sx={{
              mb: 0.5,
              color: "#10b981",
              background: "linear-gradient(195deg, #10b981, #059669)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            {t("signup.title")}
          </TypographyV1>
          <TypographyV1
            variant="body2"
            fontWeight="regular"
            sx={{ color: isDark ? "#cbd5e0" : "#718096" }}
          >
            {t("signup.subtitle")}
          </TypographyV1>
        </Box>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <Credentials
          setCredentials={setCredentials}
          credentials={credentials}
          isLoading={isLoading}
        />
        <BoxV1 mt={4} mb={1}>
          <ButtonV1
            type="submit"
            variant="contained"
            buttonColor="dark"
            fullWidth
            disabled={!canSave}
            sx={{
              backgroundColor: "#059669 !important",
              minHeight: "50px",
              fontSize: "1rem",
              textTransform: "none",
            }}
          >
            <Icon sx={{ mr: 1.5, fontSize: "1.5rem !important" }}>person_add</Icon>
            {buttonText}
          </ButtonV1>
        </BoxV1>
        <BoxV1 mt={3} textAlign="center">
          <TypographyV1 variant="button" textColor="text" fontWeight="regular">
            {t("signup.already_member")}&nbsp;
            <Link
              to="/signin"
              style={{
                color: "inherit",
                textDecoration: "none",
                fontWeight: 500,
              }}
              onMouseEnter={(e) => {
                e.target.style.textDecoration = "underline";
              }}
              onMouseLeave={(e) => {
                e.target.style.textDecoration = "none";
              }}
            >
              {t("signup.signin_cta")}
            </Link>
          </TypographyV1>
        </BoxV1>
      </form>
    </CoverLayout>
  );
}

export default SignUp;
